import { isExecutionerFajin } from './executioner_cannon_fajin'
// 請われぬ介錯・発勁の突き (exec_bladeFajin) が飛ばす光波の見た目 — 6案 (a〜f) と旧 (old) を
//   実行時に切り替える。判定・秒数・位置は一切変えない (ここは描くだけ)。
//
// 🔵 2026-09-09 便2。旧 (render.ts drawGravityOrbProjectile・紫の玉) は残し、
//   `?fajin=old` / `__setFajinLook('old')` で戻せる。
// 🔵 2026-09-10: d = ユーザー参考画像 (金白の一閃) の写し（比較用として保持）。
//   d だけ tint ('violet'=作品の紫/既定・'gold'=参考画像の金白) を切り替えられる
//   (`?fajinTint=gold|violet` / `__setFajinTint`)。a/b/c/old の色は変えない。
//
// a〜d は従来の刃先の溜め/飛行。e/f は2026-09-10 作業票6で描画を改稿:
//   240pxの丈を維持し、低αの細長い光の層＋小さい白熱芯＋ほどける微粉で作る。
//   見た目は未検収。出所と寸法の決め打ちは
//   docs/handoffs/2026-09-07-executioner-fajin-thrust-motion-spec.md §7b。
//
// 🔴 線 (stroke) 禁止 (2026-09-03 ユーザー裁定「線っぽいエフェクトは基本ダサい」を踏襲)。
//   リングも「面」= createRadialGradient でリング半径にだけ α のピークがある円を fill する。
//   外側 fill + 内側 destination-out の重ねはしない (加算合成中は抜けない)。
import type { ActiveHit, GameState } from './types'
import { ARENA } from './balance'
import { emberBurst, glowMote, hash01 } from './fx_kit'
import { et, etImg } from './exec_tint'
import { execFxSprite, execFxV1 } from './exec_fx'
import { FRONT_TIP_X, drawAdditive, fajinSpritesReady, frontSprite, ringSprite } from './exec_fx_sprites'

// 開発版の既定 (10-20): 輪と芯の質感は手描きの絵。歪み・土ぼこり・寿命・大きさはコードのまま。
function fajinSprites(): boolean { return execFxSprite() && fajinSpritesReady() }

export type FajinLook = 'old' | 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j'

// 2026-09-12: ユーザー「一旦完成」。3枚の縦振りと組み合わせた紫の衝撃波iを採用。
// 2026-10-20: 作者が手本の絵 A「重ね輪」(そざい/concept/exec-fx-1020/fajin_a.png) を選び、j を既定にした。i は `?fajin=i` で出せる。
//   同日の描き直し: j は手本 j4 (輪の中の背景が歪む・筆で掃いた縁・尖った芯・床の土ぼこり)。最初の重ね輪は DEV `?execFlame=v1`。
let fajinLook: FajinLook = 'j'
export function setFajinLook(l: FajinLook): void { fajinLook = l }
export function getFajinLook(): FajinLook { return fajinLook }

// 色語彙 (作業票が指定した既存色のみ)。
// 紫の技を替える比較版 (DEV ?purpleSwap・src/purple_swap.ts): 発勁は弾ける技なので 'parry' の色へ写す。案が無い時は下の値そのまま。
const DEEP = (): string => et('parry', '#3a0a5a')
const BRIGHT = (): string => et('parry', '#a05ad7')
const PALE = (): string => et('parry', '#c8b0ec')
const WHITE_HEX = (): string => et('parry', '#f0e8ff')
function whiteA(a: number): string { return `rgba(${et('parry', '240,232,255')},${a})` }

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * clamp01(t) }

// d だけの色スイッチ (2026-09-10)。gold = ユーザー参考画像 (金白)、violet = 作品の紫 (既定)。
export type FajinTint = 'violet' | 'gold'
let fajinTint: FajinTint = 'violet'
export function setFajinTint(t: FajinTint): void { fajinTint = t }
export function getFajinTint(): FajinTint { return fajinTint }
function paleD(): string { return fajinTint === 'gold' ? '#ffe2a8' : PALE() }
function brightD(): string { return fajinTint === 'gold' ? '#ffb347' : BRIGHT() }

// 16進色2つを t で線形補間し、アルファ a を乗せた rgba() を返す (角笛の輪の色連続変化用)。
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}
function mixColor(hexA: string, hexB: string, t: number, a: number): string {
  const [r1, g1, b1] = hexToRgb(hexA)
  const [r2, g2, b2] = hexToRgb(hexB)
  const r = Math.round(lerp(r1, r2, t))
  const g = Math.round(lerp(g1, g2, t))
  const b = Math.round(lerp(b1, b2, t))
  return `rgba(${r},${g},${b},${a})`
}

// 進行方向に細長い楕円の光の溜まり (面)。floorEllipse (quake_fx.ts) と同じ技法:
//   円を radial gradient で塗り、scale で潰して楕円にする。rx/ry は「半径」(全長/全幅の半分)。
function blobEllipse(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, color: string, a: number,
): void {
  if (a <= 0.005 || rx <= 0.3 || ry <= 0.3) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = a
  ctx.translate(cx, cy)
  ctx.scale(1, ry / rx)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx)
  g.addColorStop(0, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, rx, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// リング状の衝撃波 (面)。radial gradient の「半径 ringR にだけ α のピークがある」帯を fill する。
//   halfPlaneDir を渡すと、その向きの半面だけ (矩形クリップ) を残す = 「前へ凸の半円」。
function ringGlow(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, ringR: number, thickness: number,
  color: string, a: number, halfPlaneDir?: 1 | -1,
): void {
  if (a <= 0.005 || ringR <= 0.5) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (halfPlaneDir !== undefined) {
    const BIG = 4000
    ctx.beginPath()
    if (halfPlaneDir >= 0) ctx.rect(cx, cy - BIG, BIG, BIG * 2)
    else ctx.rect(cx - BIG, cy - BIG, BIG, BIG * 2)
    ctx.clip()
  }
  ctx.globalAlpha = a
  const outer = ringR + thickness
  const g = ctx.createRadialGradient(cx, cy, Math.max(0, ringR - thickness), cx, cy, outer)
  g.addColorStop(0, 'rgba(0,0,0,0)')
  g.addColorStop(0.55, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(cx, cy, outer, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// spec §7b・作業票6。寸法と拍は作業票5のまま、面の作りだけを置き換える。
// 全て描画専用。engineの760ms/座標/速度/判定には書き戻さない。
const WIND = {
  heightPx: 240, depthPx: 230, holdSpanPx: 105,
  residueMs: 680, foldFadeMs: 180, wakeStepPx: 76,
  strandCount: 36, coreCount: 12, edgeCount: 18, edgeLifeMs: 120,
  bossClearPx: 62, bossFadePx: 84, bossAlpha: 0.08,
} as const

// ボス中心〜刃を含む帯に面の一部でも届くなら、その面全体を薄くする。
// 中心距離だけで判定すると長い面の端がボスを覆うため、横半径も含める。
// 反射後・残滓にも現在のboss.xを使う。描画単独呼出しでは発生点を保護する。
function windBossAlpha(x: number, rx: number, bossX: number): number {
  const gap = Math.max(0, Math.abs(x - bossX) - rx)
  const t = clamp01((gap - WIND.bossClearPx) / WIND.bossFadePx)
  return lerp(WIND.bossAlpha, 1, t * t * (3 - 2 * t))
}

// 一枚は進行方向に長い楕円。傾きと上下幅を別にし、radialの透明端と外形を一致させる。
// 境界に色が残るBezierクリップ、大きな下塗り、消去合成は使わない。
function windStrand(
  ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number,
  slope: number, color: string, alpha: number, bossX: number,
): void {
  if (alpha <= 0 || rx <= 0 || ry <= 0) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = alpha * windBossAlpha(x, rx, bossX)
  ctx.transform(rx, slope * rx, 0, ry, x, y)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1)
  g.addColorStop(0, color)
  g.addColorStop(0.3, mixColor(color, color, 0, 0.88))
  g.addColorStop(0.65, mixColor(color, color, 0, 0.46))
  g.addColorStop(0.86, mixColor(DEEP(), DEEP(), 0, 0.10))
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, 1, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// 筋36枚+白熱芯12枚の本体 (丈240pxの光の塊)。drawWindFlight (飛行中・alphaScale=1) と
//   drawWindHold (溜め中・alphaScale<1で少し控える) の両方から呼ぶ。位置式は共通。
function drawWindMass(
  ctx: CanvasRenderingContext2D, look: 'e' | 'f', floor: number, age: number, seed: number,
  bossX: number, alphaScale: number,
): void {
  const h = WIND.heightPx
  const d = WIND.depthPx
  // 36枚を丈全体へ散らす。eは上側が後方へ巻き返り、fは3束の斜めの流れが重なる。
  // 各面の縦外接幅を先に引くことで、傾きが揺れても全体の丈は発射時から240px。
  for (let i = 0; i < WIND.strandCount; i += 1) {
    const t = i / (WIND.strandCount - 1)
    const u = hash01(seed + i * 13)
    const v = hash01(seed + i * 23 + 3)
    const sway = Math.sin(age * 0.014 + i * 2.4) * 0.035
    const slope = look === 'e' ? -0.26 + 0.66 * t + sway
      : [-0.26, 0.16, -0.12][i % 3]! + sway
    const rx = d * (0.18 + u * 0.12)
    const ry = 5 + v * 7
    const vertical = Math.hypot(slope * rx, ry)
    const x = look === 'e' ? -34 - d * 0.47 * t * t - u * 35
      : -36 - (i % 3) * 40 - t * 34 - u * 17
    const y = floor - vertical - t * (h - 2 * vertical)
    windStrand(ctx, x, y, rx, ry, slope, i % 5 === 0 ? PALE() : BRIGHT(),
      (0.15 + v * 0.07) * alphaScale, bossX)
  }
  // 芯の外接範囲は約90×75px（230×240pxの約12%）。単一の白い面を置かず、
  // 12枚の白紫が重なった中心だけ白熱する。横断する芯線にはしない。
  for (let i = 0; i < WIND.coreCount; i += 1) {
    const u = hash01(seed + i * 31)
    const v = hash01(seed + i * 43)
    const x = -24 + u * 17
    const y = floor - h * 0.47 + (v - 0.5) * 22
    const slope = look === 'e' ? -0.18 + v * 0.36 : (i % 2 ? 0.22 : -0.22)
    windStrand(ctx, x, y, 32 + u * 10, 22 + v * 4, slope, WHITE_HEX(),
      (0.16 + u * 0.06) * alphaScale, bossX)
  }
}

function drawWindHold(
  ctx: CanvasRenderingContext2D, look: 'e' | 'f', floor: number, progress: number, seed: number,
  bossX: number,
): void {
  // 保持中は発生点付近の地表の薄い煙4枚だけ。光は育てない (drawWindMassは飛行専用)。
  const fade = 0.3 + 0.7 * (1 - progress)
  for (let i = 0; i < 4; i += 1) {
    const h = hash01(seed + i * 17)
    const x = 8 + h * WIND.holdSpanPx
    windStrand(ctx, x - 12, floor - 12 - h * 15, 22 + h * 10, 5 + h * 4,
      -0.08, look === 'e' ? BRIGHT() : DEEP(), (0.13 + h * 0.08) * fade, bossX)
    glowMote(ctx, x - 7, floor - 5 - h * 16, 1.5, PALE(),
      0.35 * fade * windBossAlpha(x - 7, 1.5, bossX))
  }
}

function drawWindFlight(
  ctx: CanvasRenderingContext2D, look: 'e' | 'f', floor: number, age: number, seed: number,
  bossX: number,
): void {
  const h = WIND.heightPx
  drawWindMass(ctx, look, floor, age, seed, bossX, 1)
  // 前縁と上縁から18粒。各粒は120ms以内で先に消え、壁時計で再発生しない。
  for (let i = 0; i < WIND.edgeCount; i += 1) {
    const u = hash01(seed + i * 47)
    const v = hash01(seed + i * 59)
    const life = WIND.edgeLifeMs * (0.55 + v * 0.45)
    const p = clamp01(age / life)
    const x = i % 2 === 0 ? 12 + u * 15 + p * 22 : -30 - u * 125 + p * 12
    const y = floor - (i % 2 === 0 ? 25 + v * 175 : h - 8) - p * 14
    const r = (0.8 + u * 1.3) * (1 - p * 0.4)
    glowMote(ctx, x, y, r, PALE(), 0.22 * (1 - p) ** 2 * windBossAlpha(x, r, bossX))
  }
}

function drawWindWake(
  ctx: CanvasRenderingContext2D, look: 'e' | 'f', floor: number, travelled: number,
  speed: number, sinceSeenMs: number, seed: number, bossX: number,
): void {
  // owner=windFrames。襞180ms、煙680msで0。命中後も観測済み地点だけに残す。
  const length = Math.min(travelled, speed * WIND.residueMs / 1000)
  const step = WIND.wakeStepPx
  const first = Math.max(0, Math.floor((travelled - length) / step))
  const last = Math.floor(travelled / step)
  for (let i = first; i <= last; i += 1) {
    const at = i === 0 ? 0 : (i - 0.27 + hash01(seed + i * 11) * 0.54) * step
    if (at > travelled) continue
    const back = travelled - at
    const age = sinceSeenMs + back / Math.max(1, speed) * 1000
    const fade = 1 - clamp01(age / WIND.residueMs)
    if (fade <= 0) continue
    const x = -back
    for (let j = 0; j < 6; j += 1) {
      const p = hash01(seed + i * 37 + j * 19)
      windStrand(ctx, x - 25 - p * 52, floor - 20 - j * 30,
        30 + p * 23, 9 + p * 5, look === 'e' ? j * 0.055 : -0.12,
        BRIGHT(), 0.045 * fade * fade, bossX)
    }
    const foldFade = 1 - clamp01(age / WIND.foldFadeMs)
    for (let j = 0; j < 4; j += 1) {
      windStrand(ctx, x - 22 - j * 17, floor - 45 - j * 48, 36, 7,
        look === 'e' ? 0.12 + j * 0.05 : -0.2,
        PALE(), 0.07 * foldFade, bossX)
    }
    for (let j = 0; j < 4; j += 1) {
      const p = hash01(seed + i * 41 + j * 19)
      const px = x - 10 - p * 75
      glowMote(ctx, px, floor - 16 - hash01(seed + i * 17 + j * 7) * 156,
        1.2 + p, PALE(), 0.18 * fade * windBossAlpha(px, 1.2 + p, bossX))
    }
  }
}

// g: 2026-09-11。突きの残圧 → 厚い曲面 → 通過地点でほどける余韻。
// 参照動画から借りるのは面の変形と大小の流れ。二段攻撃・地面の判定は追加しない。
// 一枚の曲面を透明な径方向勾配と角度方向の包絡で塗る。閉じた輪にはしない。
function pressureFold(
  ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number,
  bend: number, start: number, span: number, color: string, alpha: number,
): void {
  if (alpha <= 0.002) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.transform(rx, bend * ry, 0, ry, x, y)
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1)
  gradient.addColorStop(0, 'rgba(0,0,0,0)')
  gradient.addColorStop(0.22, 'rgba(0,0,0,0)')
  gradient.addColorStop(0.46, mixColor(color, color, 0, 0.65))
  gradient.addColorStop(0.70, color)
  gradient.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = gradient
  const slices = 14
  for (let i = 0; i < slices; i += 1) {
    const u = (i + 0.5) / slices
    ctx.globalAlpha = alpha * Math.sin(Math.PI * u) ** 0.65
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.arc(0, 0, 1, start + span * i / slices, start + span * (i + 1) / slices)
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

function drawPressureHold(ctx: CanvasRenderingContext2D, age: number, delay: number): void {
  // owner=待機hit。最初だけ鋭く開き、その後は弱い歪みを維持する。収束も球の成長もない。
  const strike = (1 - clamp01(age / 150)) ** 2
  const end = clamp01((age / Math.max(1, delay) - 0.84) / 0.16)
  const a = 0.10 + strike * 0.48 + end * 0.08
  pressureFold(ctx, -10, -3, 16 + 8 * (1 - strike), 31, -0.12,
    -1.55, 2.9, BRIGHT(), a)
  pressureFold(ctx, -5, 4, 25, 21, 0.18, -1.1, 2.1, PALE(), a * 0.65)
  if (strike > 0) blobEllipse(ctx, 17, 0, 31, 3.5, PALE(), strike * 0.55)
}

function drawPressureFront(ctx: CanvasRenderingContext2D, age: number, seed: number): void {
  // 前面の中心を判定の現在位置に置く。丈は発射から読め、平行移動だけでなく曲率が変わる。
  const spread = 1 + 0.12 * clamp01(age / 400)
  pressureFold(ctx, -21, -3, 46, 79 * spread, -0.08 + Math.sin(age * 0.009) * 0.08,
    -1.65, 3.2, BRIGHT(), 0.92)
  pressureFold(ctx, -13, 5, 23, 61 * spread, 0.1,
    -1.35, 2.55, PALE(), 0.76)
  // 上下は異なる曲率で巻き返る。中心と端のどちらにも丸いコアを作らない。
  for (let i = 0; i < 3; i += 1) {
    const u = hash01(seed + i * 29)
    pressureFold(ctx, -47 - i * 21, (i % 2 ? 1 : -1) * (15 + u * 12),
      48 + u * 15, (42 + i * 13) * spread, Math.sin(age * 0.013 + i) * 0.18,
      -1.8 + i * 0.36, 2.4 + u * 0.5, i === 0 ? PALE() : BRIGHT(), 0.48 - i * 0.07)
  }
  // 曲面の内側を不揃いな薄い断片で満たす。乱数はhitに固定し、時間には連続で従う。
  for (let i = 0; i < 15; i += 1) {
    const u = hash01(seed + i * 37), v = hash01(seed + i * 53)
    const x = -16 - u * 96 - Math.sin(age * 0.012 + i) * 7
    const y = (v - 0.5) * 102 * spread
    pressureFold(ctx, x, y, 13 + v * 23, 8 + u * 17,
      Math.sin(i + age * 0.008) * 0.6, -1.5 + u, 2 + v, i % 4 ? BRIGHT() : PALE(), 0.46)
    // 乱れた圧の厚み。点光源に見えないように、曲面の内側に扁平な面を重ねる。
    blobEllipse(ctx, x - 8, y, 17 + v * 12, 5 + u * 7, i % 3 ? BRIGHT() : PALE(), 0.24)
  }
  // 短い直進圧。一閃は先端近くに限り、発生点と光線で接続しない。
  blobEllipse(ctx, -17, 0, 32, 4, WHITE_HEX(), 0.54)
}

function drawPressureWake(
  ctx: CanvasRenderingContext2D, travelled: number, speed: number, sinceSeenMs: number, seed: number,
): void {
  // owner=windFrames、寿命680ms。通過済みの地点だけに描く。後方の余韻は無判定。
  const step = 68
  const length = Math.min(travelled, speed * WIND.residueMs / 1000)
  const first = Math.max(0, Math.floor((travelled - length) / step))
  const last = Math.floor(travelled / step)
  for (let i = first; i <= last; i += 1) {
    const at = i * step
    const back = travelled - at
    const age = sinceSeenMs + back / Math.max(1, speed) * 1000
    const p = clamp01(age / WIND.residueMs)
    const fade = (1 - p) ** 2
    if (fade <= 0) continue
    const u = hash01(seed + i * 31)
    // 位置は固定。前面が離れるほど帯が開き、光が弱まる。
    const open = clamp01(age / 200)
    pressureFold(ctx, -back - 24, -8 + u * 17, 35 + open * 18, 51 + open * 32,
      -0.18 + u * 0.4 + open * 0.12, -1.9 + u * 0.7, 2.6,
      BRIGHT(), 0.36 * fade)
    const innerFade = (1 - clamp01(age / 200)) ** 2
    pressureFold(ctx, -back - 16, 5, 24, 48, -0.2, -1.25, 2.1, PALE(), 0.20 * innerFade)
    for (let j = 0; j < 3; j += 1) {
      const v = hash01(seed + i * 43 + j * 17)
      blobEllipse(ctx, -back - 30 - v * 35, (j - 1) * 35 - p * 10,
        24 + p * 11, 7 + v * 9, BRIGHT(), 0.035 * fade)
      glowMote(ctx, -back - 12 - v * 35, (v - 0.5) * 114 - p * 13,
        0.8 + v * 0.7, PALE(), 0.16 * fade)
    }
  }
}

type WindTrace = { hit: ActiveHit; originX: number; seenAt: number }
// h = A「空間が順に押し開かれる」。面の発生点は道に固定し、年齢で開いて消す。
// g の乱れた塊を運ぶ処理は比較用に保持。hitや技の時刻・位置には書き戻さない。
const UNFOLD = { spacing: 86, lifeMs: 300 } as const

function unfoldingMembrane(
  ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number,
  bend: number, start: number, span: number, alpha: number, bright: number,
): void {
  if (alpha < 0.003) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.transform(rx, bend * ry, 0, ry, x, y)
  // 広い紫の厚みから前縁だけが白紫へ立つ。中心を埋める光源は置かない。
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1)
  gradient.addColorStop(0, 'rgba(0,0,0,0)')
  gradient.addColorStop(0.15, 'rgba(0,0,0,0)')
  gradient.addColorStop(0.36, mixColor(DEEP(), BRIGHT(), 0.55, 0.38))
  gradient.addColorStop(0.62, mixColor(BRIGHT(), PALE(), bright * 0.35, 0.85))
  gradient.addColorStop(0.85, mixColor(BRIGHT(), WHITE_HEX(), bright * 0.78, 1))
  gradient.addColorStop(0.96, mixColor(BRIGHT(), PALE(), bright * 0.3, 0.34))
  gradient.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = gradient
  const slices = 40
  for (let i = 0; i < slices; i += 1) {
    const u = (i + 0.5) / slices
    // 面の中央を丸い発光点にしない。厚みを保ち、切れ端だけ短くほどく。
    const edge = Math.min(1, u / 0.16, (1 - u) / 0.18)
    ctx.globalAlpha = alpha * edge * edge * (3 - 2 * edge)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.arc(0, 0, 1, start + span * i / slices, start + span * (i + 1) / slices + 0.001)
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

function drawUnfoldingHold(ctx: CanvasRenderingContext2D, age: number): void {
  const impact = (1 - clamp01(age / 100)) ** 2
  // 突いた瞬間に折れ目が開き、既存の一拍の間は細く弱い面だけを残す。
  unfoldingMembrane(ctx, -8, 0, 11 + impact * 18, 24 + impact * 20,
    -0.1, -1.3, 2.6, 0.12 + impact * 0.7, impact)
}

function drawUnfoldingPath(
  ctx: CanvasRenderingContext2D, travelled: number, speed: number, sinceSeenMs: number,
): void {
  const step = UNFOLD.spacing
  // 長い飛行でも生きている区間だけ。反射後は描画履歴の新しい起点から再開する。
  const first = Math.max(0, Math.floor((travelled - speed * UNFOLD.lifeMs / 1000) / step) - 1)
  const last = Math.floor(travelled / step)
  for (let i = first; i <= last; i += 1) {
    // 手で組んだ大小の拍。等間隔・同じ形の輪のトンネルにしない。
    const at = i * step + [0, 10, -7][i % 3]!
    if (at > travelled) continue
    const back = travelled - at
    const age = back / Math.max(1, speed) * 1000 + sinceSeenMs
    if (age >= UNFOLD.lifeMs) continue
    const rise = 1 - (1 - clamp01(age / 28)) ** 3
    const open = clamp01((age - 24) / 150)
    const fade = (1 - clamp01((age - 20) / 230)) ** 2
    const bright = (1 - clamp01(age / 88)) ** 2
    const rx = 40 + 72 * open
    const ry = (42 + 42 * rise + 27 * open) * [1, 0.88, 1.08][i % 3]!
    const x = -back - 38
    // 中央が抜け、上下の縁が別々に反り返る。同じ面の前進ではなく、その場での展開。
    const split = open * 1.0
    const upperSpan = 1.55 - split
    unfoldingMembrane(ctx, x, -open * 20, rx, ry, -0.13 - open * 0.5,
      -1.58 - open * 0.40, upperSpan, fade * 0.95, bright)
    unfoldingMembrane(ctx, x, open * 16, rx * (1 - open * 0.12), ry * 0.88,
      0.08 + open * 0.4, 0.03 + split, 1.43 - split * 0.7, fade * 0.78, bright * 0.85)
  }
}

// i = 2026-09-11承認画像「空気を叩き抜く衝撃波」。圧縮面と裂ける空気を面で描く。
// 多角形の範囲に径方向勾配を塗る。輪郭を線で描かず、芯や煙の円を重ねない。
type ShockPoint = readonly [number, number]
function shockPatch(
  ctx: CanvasRenderingContext2D, points: readonly ShockPoint[], color: string, alpha: number,
): void {
  if (alpha <= 0.003) return
  const xs = points.map(p => p[0]), ys = points.map(p => p[1])
  const left = Math.min(...xs), right = Math.max(...xs)
  const top = Math.min(...ys), bottom = Math.max(...ys)
  const rx = Math.max(1, (right - left) * 0.64), ry = Math.max(1, (bottom - top) * 0.64)
  const cx = (left + right) / 2, cy = (top + bottom) / 2
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = alpha
  ctx.translate(cx, cy)
  ctx.scale(rx, ry)
  ctx.beginPath()
  const first = points[0]!
  ctx.moveTo((first[0] - cx) / rx, (first[1] - cy) / ry)
  for (let n = 1; n < points.length; n += 1) {
    const prev = points[n - 1]!, next = points[n]!
    ctx.quadraticCurveTo(((prev[0] + next[0]) / 2 - cx) / rx,
      ((prev[1] + next[1]) / 2 - cy) / ry, (next[0] - cx) / rx, (next[1] - cy) / ry)
  }
  ctx.closePath()
  ctx.clip()
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1)
  gradient.addColorStop(0, color)
  gradient.addColorStop(0.35, mixColor(color, color, 0, 0.93))
  gradient.addColorStop(0.74, mixColor(color, color, 0, 0.52))
  gradient.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(0, 0, 1, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function shockFrontPoint(theta: number, age: number): ShockPoint {
  const fringe = Math.sin(theta * 7 - age * 0.021) * Math.sin(theta * 3 + age * 0.009)
  return [-54 * (1 - Math.cos(theta)) - Math.abs(fringe) * 7 - 7 * Math.exp(-theta * theta / 0.016),
    92 * Math.sin(theta) + fringe * Math.abs(theta) * 3]
}

// 根元を圧縮面につなぎ、細かい裂け目をもつ先細りの面にする。
function shockRip(ctx: CanvasRenderingContext2D, x: number, y: number,
  length: number, out: number, width: number, seed: number, alpha: number): void {
  const upper: ShockPoint[] = [], lower: ShockPoint[] = []
  for (let j = 0; j <= 16; j += 1) {
    const t = j / 16
    const ripple = Math.sin(j * 2.71 + seed) * Math.sin(j * 1.39 - seed)
    const cy = y + out * t ** 0.85 + ripple * width * 0.4 * Math.sin(Math.PI * t)
    const half = width * (1 - t) ** 0.8 * (0.35 + 0.65 * Math.abs(Math.sin(j * 1.81 + seed)))
    upper.push([x - length * t, cy - half])
    lower.push([x - length * t + ripple * 7 * Math.sin(Math.PI * t), cy + half])
  }
  shockPatch(ctx, [...upper, ...lower.reverse()], PALE(), alpha)
}

function drawCompressionFront(ctx: CanvasRenderingContext2D, age: number): void {
  const front: ShockPoint[] = [], rear: ShockPoint[] = []
  for (let n = 0; n <= 24; n += 1) {
    const theta = -1.42 + n / 24 * 2.84
    const [x, y] = shockFrontPoint(theta, age)
    front.push([x, y])
    rear.push([x - 14 - Math.abs(theta) * 14, y * 0.92])
  }
  // 一つの圧縮面。その後方の枝はこの面から出るので、独立した扇形にならない。
  shockPatch(ctx, [...front, ...rear.reverse()], PALE(), 0.22)
  for (const side of [-1, 1]) {
    for (let n = 0; n < 10; n += 1) {
      const theta = side * (0.045 + n * 0.135)
      const [x, y] = shockFrontPoint(theta, age)
      const [nx, ny] = shockFrontPoint(theta + side * 0.15, age)
      const pulse = 0.7 + Math.sin(age * 0.032 - n * 0.87) * 0.3
      // 前縁の白紫は細い弧を一周させず、圧縮された短い面の集合にする。
      shockPatch(ctx, [[x, y], [nx, ny], [nx - 9 - pulse * 7, ny - side * 2],
        [x - 5 - pulse * 3, y + side * 3]], WHITE_HEX(), 0.28 + pulse * 0.12)
      // 付着した空気が後ろへ伸び、外向きに裂ける。生滅をゼロで繋ぎ、固定模様を運ばない。
      const p = ((age + n * 17 + (side > 0 ? 31 : 0)) % 180) / 180
      const life = Math.sin(Math.PI * p) ** 0.8
      const length = 55 + p * (110 + n * 4)
      const out = side * (3 + n * 2.0 + p * (8 + n * 3.8))
      const width = (13 + n * 0.9) * (1 - p * 0.45)
      const seed = n * 7.13 + side * 2 + age * 0.012
      shockRip(ctx, x - 4, y, length, out, width, seed, life * 0.64)
      // 主流からさらに二股に裂ける。位置と色の乱数ではなく流れの形を変える。
      shockRip(ctx, x - length * 0.25, y + out * 0.30, length * 0.70,
        out * 0.95 + side * 15, width * 0.55, seed + 4, life * 0.46)
      shockRip(ctx, x - 3, y, length * 0.45, out * 0.5,
        width * 0.26, seed + 7, life * 0.5)
    }
  }
}

function drawCompressionHold(ctx: CanvasRenderingContext2D, age: number): void {
  const strike = (1 - clamp01(age / 100)) ** 2
  // 突きに反応する短い圧。760msの保持中に玉や長いビームへ育てない。
  ctx.save()
  ctx.scale(0.42, 0.42)
  shockPatch(ctx, [[0, 0], [-17, -28 - strike * 14], [-9, -5], [-38, -3],
    [-10, 4], [-23, 29 + strike * 10]], PALE(), 0.13 + strike * 0.55)
  ctx.restore()
}

const COMPRESSION_WAKE_MS = 240 // 描画履歴ownerの680ms以内で必ず0へ。
function drawCompressionWake(
  ctx: CanvasRenderingContext2D, travelled: number, speed: number, sinceSeenMs: number,
): void {
  const step = 42
  const first = Math.max(0, Math.floor((travelled - speed * COMPRESSION_WAKE_MS / 1000) / step))
  for (let n = first; n <= Math.floor(travelled / step); n += 1) {
    const back = travelled - n * step
    const age = sinceSeenMs + back / Math.max(1, speed) * 1000
    if (age >= COMPRESSION_WAKE_MS) continue
    const p = clamp01(age / COMPRESSION_WAKE_MS)
    const fade = Math.sin(Math.PI * clamp01(age / 40) / 2) * (1 - p) ** 2
    const x = -back - 30
    for (const side of [-1, 1]) {
      const y = side * (55 + 40 * (1 - (1 - p) ** 3))
      const width = 10 * (1 - p)
      const tail = 34 + p * 44
      shockPatch(ctx, [[x, y], [x - tail * 0.36, y + side * width],
        [x - tail * 0.53, y + side * (width + 8)], [x - tail, y + side * (14 + p * 15)],
        [x - tail * 0.49, y + side * 3], [x - 8, y - side * width]],
      PALE(), 0.24 * fade)
    }
  }
}

// ───────────────────────── j = A「重ね輪」(2026-10-20 手本 fajin_a.png) ─────────────────────────
// 前端は白紫の二重の矢じり。通った道に半月の輪が一定の間隔で生まれ、前へ膨らんだ形のまま広がって薄れる。
// 放った直後が一番強く、進むほど輪は大きく薄くなる。輪が床に届くと土ぼこりが立つ。光の塊・線にはしない (面だけ)。
const RINGS = { step: 96, lifeMs: 520, baseRy: 52, growByPath: 0.12, growByAge: 0.22, maxRy: 250 } as const

function ringStrength(flightMs: number): number { return 1 - 0.5 * clamp01(flightMs / 700) }

// 前へ膨らむ半月 (x = 0 が輪の背、+x が前)。edge = 縁の厚み。
function drawHalfRing(ctx: CanvasRenderingContext2D, x: number, ry: number, alpha: number, floor: number): void {
  if (alpha <= 0.01) return
  const rx = ry * 0.62
  const outer: ShockPoint[] = [], inner: ShockPoint[] = [], dome: ShockPoint[] = []
  for (let n = 0; n <= 20; n += 1) {
    const th = -Math.PI / 2 + n / 20 * Math.PI
    const c = Math.cos(th), s = Math.sin(th)
    outer.push([x + rx * c, ry * s])
    inner.push([x + rx * 0.5 * c - 6, ry * 0.96 * s])
    dome.push([x + rx * 0.9 * c, ry * 0.95 * s])
  }
  ctx.save()
  // 床より下は描かない (輪は床で切れる)。
  ctx.beginPath()
  ctx.rect(x - 4000, -4000, 8000, 4000 + floor)
  ctx.clip()
  shockPatch(ctx, [...dome, [x - rx * 0.6, ry * 0.85], [x - rx * 0.6, -ry * 0.85]], DEEP(), alpha * 0.7)
  shockPatch(ctx, [...dome, [x - rx * 0.2, ry * 0.8], [x - rx * 0.2, -ry * 0.8]], BRIGHT(), alpha * 0.26)
  shockPatch(ctx, [...outer, ...inner.reverse()], BRIGHT(), alpha * 0.85)
  // 縁のいちばん外だけ白紫に立てる (細い面・線にはしない)。
  const rim: ShockPoint[] = [], rimIn: ShockPoint[] = []
  for (let n = 0; n <= 16; n += 1) {
    const th = -1.25 + n / 16 * 2.5
    rim.push([x + rx * Math.cos(th), ry * Math.sin(th)])
    rimIn.push([x + rx * 0.78 * Math.cos(th) - 2, ry * 0.98 * Math.sin(th)])
  }
  shockPatch(ctx, [...rim, ...rimIn.reverse()], PALE(), alpha * 0.65)
  ctx.restore()
}

// 輪が床に触れた所の土ぼこり (通常合成の灰紫の塊)。
function drawRingDust(ctx: CanvasRenderingContext2D, x: number, floor: number, p: number, seed: number, alpha: number): void {
  if (alpha <= 0.01) return
  ctx.save()
  for (let n = 0; n < 5; n += 1) {
    const h = hash01(seed + n * 3.1)
    const dx = x - 10 - n * 16 - p * 30 * h
    const r = (10 + h * 14) * (0.6 + p * 0.9)
    ctx.globalAlpha = alpha * (1 - p) * 0.4
    const g = ctx.createRadialGradient(dx, floor - r * 0.5, 0, dx, floor - r * 0.5, r)
    g.addColorStop(0, 'rgba(120,108,132,0.9)')
    g.addColorStop(1, 'rgba(60,52,70,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.ellipse(dx, floor - r * 0.45, r * 1.4, r * 0.6, 0, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

function drawStackedRings(
  ctx: CanvasRenderingContext2D, travelled: number, speed: number, sinceSeenMs: number,
  flightMs: number, floor: number, seed: number,
): void {
  const strength = ringStrength(flightMs)
  const first = Math.max(0, Math.floor((travelled - speed * RINGS.lifeMs / 1000) / RINGS.step))
  for (let n = first; n <= Math.floor(travelled / RINGS.step); n += 1) {
    const back = travelled - n * RINGS.step
    const age = sinceSeenMs + back / Math.max(1, speed) * 1000
    if (age >= RINGS.lifeMs) continue
    const p = clamp01(age / RINGS.lifeMs)
    const ry = Math.min(RINGS.maxRy, RINGS.baseRy + n * RINGS.step * RINGS.growByPath + age * RINGS.growByAge)
    const fadeIn = clamp01(age / 35)
    const a = fadeIn * (1 - p) ** 1.3 * strength * (n === 0 ? 0.6 : 1)
    const x = -back - 20
    drawHalfRing(ctx, x, ry, a, floor)
    if (ry * 0.95 >= floor) drawRingDust(ctx, x, floor, p, seed + n * 11.3, a * 1.6)
    // 輪のまわりに舞う紫の欠片。
    for (let k = 0; k < 3; k += 1) {
      const h = hash01(seed + n * 5.7 + k)
      const th = -1.3 + hash01(seed + n * 2.3 + k * 9) * 2.6
      const fx = x + ry * 0.5 * Math.cos(th) + p * (8 + h * 30)
      const fy = ry * Math.sin(th) * (1 + p * 0.25) - p * 18
      if (fy > floor) continue
      shockPatch(ctx, [[fx + 4, fy], [fx, fy + 1.6], [fx - 3, fy], [fx, fy - 1.6]], BRIGHT(), a * 0.9)
    }
  }
}

function drawRingFront(ctx: CanvasRenderingContext2D, flightMs: number): void {
  const s = ringStrength(flightMs)
  const flare = 1 + 0.6 * (1 - clamp01(flightMs / 90))
  glowMote(ctx, -6, 0, 46 * flare, BRIGHT(), 0.4 * s)
  glowMote(ctx, 0, 0, 18 * flare, whiteA(0.9), 0.75 * s)
  for (const [off, scale, a] of [[0, 1, 0.95], [-26, 0.82, 0.6]] as const) {
    const L = 46 * scale * flare, H = 34 * scale * flare
    // 二重の矢じり (山形の面)。先が白紫、翼は紫へ抜ける。
    shockPatch(ctx, [[off + 14, 0], [off - L, -H], [off - L * 0.55, -H * 0.18],
      [off - L * 0.95, 0], [off - L * 0.55, H * 0.18], [off - L, H]], WHITE_HEX(), a * s)
    shockPatch(ctx, [[off + 6, 0], [off - L * 1.1, -H * 1.15], [off - L * 0.7, 0], [off - L * 1.1, H * 1.15]],
      BRIGHT(), a * 0.5 * s)
  }
}

function drawRingHold(ctx: CanvasRenderingContext2D, elapsedMs: number, launchDelayMs: number): void {
  const p = clamp01((elapsedMs - (launchDelayMs - 260)) / 260)
  if (p <= 0) return
  glowMote(ctx, 0, 0, 10 + p * 16, BRIGHT(), 0.5 * p)
  glowMote(ctx, 0, 0, 4 + p * 6, whiteA(0.9), 0.7 * p)
}

// ───────────────────────── j の描き直し = 手本 j4 (2026-10-20 2回目の答え・そざい/concept/exec-fx-1020/fajin_j4.png) ─────────────────────────
// 作者が描き直しの6枚から j4 を選んだ (答えるページ redo_fajin = j4)。前の重ね輪 (上の drawStackedRings) は DEV `?execFlame=v1`。
//   輪の内側は背景が透けて歪む (空気の屈折)。縁は筆で掃いたような途切れた面で、外へ欠片になってほどける。
//   前端は先の尖った淡い紫の芯。進んだ道の床に土ぼこりが立ち、広がって残る。
let refractScratch: HTMLCanvasElement | null = null

/** pts で囲んだ範囲の後ろ (いま描かれている背景・人物) を横の帯ごとにずらして描き直す = 透けて歪んで見える。 */
function refractInside(ctx: CanvasRenderingContext2D, pts: readonly ShockPoint[], amp: number, seed: number, alpha: number): void {
  if (alpha <= 0.02 || typeof document === 'undefined') return
  const source = ctx.canvas as HTMLCanvasElement | undefined
  const m = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null
  if (!source || typeof source.width !== 'number' || !m || typeof m.a !== 'number') return
  const dev = pts.map(([x, y]) => [m.a * x + m.c * y + m.e, m.b * x + m.d * y + m.f] as const)
  const x0 = Math.max(0, Math.floor(Math.min(...dev.map(q => q[0]))) - 2)
  const y0 = Math.max(0, Math.floor(Math.min(...dev.map(q => q[1]))) - 2)
  const x1 = Math.min(source.width, Math.ceil(Math.max(...dev.map(q => q[0]))) + 2)
  const y1 = Math.min(source.height, Math.ceil(Math.max(...dev.map(q => q[1]))) + 2)
  const w = x1 - x0, h = y1 - y0
  if (w < 4 || h < 4) return
  if (!refractScratch) refractScratch = document.createElement('canvas')
  if (refractScratch.width < w || refractScratch.height < h) {
    refractScratch.width = Math.max(refractScratch.width, w)
    refractScratch.height = Math.max(refractScratch.height, h)
  }
  const sctx = refractScratch.getContext('2d')
  if (!sctx) return
  sctx.setTransform(1, 0, 0, 1, 0, 0)
  sctx.globalCompositeOperation = 'copy'
  sctx.drawImage(source, x0, y0, w, h, 0, 0, w, h)
  sctx.globalCompositeOperation = 'source-over'
  const scale = Math.hypot(m.a, m.b)
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.beginPath()
  ctx.moveTo(dev[0]![0], dev[0]![1])
  // 切り抜きの形だけ (線は描かない)。shockPatch と同じく中点を通る曲線でつなぐ。
  for (let n = 1; n < dev.length; n += 1) {
    const a = dev[n - 1]!, b = dev[n]!
    ctx.quadraticCurveTo((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, b[0], b[1])
  }
  ctx.closePath()
  ctx.clip()
  ctx.globalAlpha = Math.min(1, alpha)
  const bands = 9
  const bh = Math.ceil(h / bands)
  for (let i = 0; i < bands; i += 1) {
    const sy = i * bh
    const sh = Math.min(bh, h - sy)
    if (sh <= 0) break
    const dx = (Math.sin(i * 1.7 + seed) * 0.7 + Math.sin(i * 0.6 - seed * 0.5) * 0.3) * amp * scale
    // 横へ少し引き伸ばして描く = 輪が空気を押して背景がにじむ。
    ctx.drawImage(refractScratch, 0, sy, w, sh, x0 + dx - w * 0.03, y0 + sy, w * 1.06, sh)
  }
  ctx.restore()
}

// 筆で掃いた縁: 弧に沿った短い面を並べ、ところどころ途切れさせる。外側は欠片になってほどける。
function brushArc(
  ctx: CanvasRenderingContext2D, x: number, rx: number, ry: number, p: number, alpha: number, seed: number, floor: number,
): void {
  // 筆の重ね: 手本の輪は1本の縁ではなく、少しずつ後ろへずれた2〜3筆が重なって厚い三日月になる。
  // 10-20 撮影: 1筆・細い斑ではゲームの大きさでほぼ見えなかった → 筆を重ね、太く濃く。
  const N = 11
  for (let layer = 0; layer < 3; layer += 1) {
    const lr = 1 - layer * 0.07
    const la = layer === 0 ? 1 : layer === 1 ? 0.75 : 0.5
    for (let k = 0; k < N; k += 1) {
      const h = hash01(seed + layer * 97 + k * 3.3)
      if (h < 0.14) continue // 途切れ
      const th0 = -1.4 + (k / N) * 2.8
      const th1 = th0 + (2.8 / N) * (1.1 + hash01(seed + layer * 31 + k * 5.1) * 0.7)
      const thick = (3 + hash01(seed + layer * 13 + k * 7.7) * 5) * (1 - p * 0.35)
      const out = (hash01(seed + layer * 7 + k * 2.9) - 0.5) * 8
      const front: ShockPoint[] = [], rear: ShockPoint[] = []
      for (let j = 0; j <= 8; j += 1) {
        const th = th0 + (th1 - th0) * (j / 8)
        const taper = Math.sin(Math.PI * (j / 8)) ** 0.8
        const r = lr + out / Math.max(20, rx)
        const cx = x + rx * r * Math.cos(th) - layer * 10, cy = ry * r * Math.sin(th)
        front.push([cx + thick * 0.5 * taper, cy])
        rear.push([cx - thick * 1.8 * taper - 5, cy])
      }
      if (front.every(q => q[1] > floor)) continue
      const outer = layer === 0 && Math.abs(th0) < 0.9
      shockPatch(ctx, [...front, ...rear.reverse()], outer && h > 0.8 ? PALE() : BRIGHT(), alpha * la * (0.55 + h * 0.35))
    }
  }
  // ほどける欠片: 縁から外・後ろへ流れて薄れる。
  for (let k = 0; k < 22; k += 1) {
    const h = hash01(seed + 40 + k * 4.9)
    const th = -1.3 + hash01(seed + 60 + k * 2.1) * 2.6
    const drift = p * (14 + h * 40)
    const fx = x + rx * Math.cos(th) + drift * 0.6 - h * 12
    const fy = ry * Math.sin(th) * (1 + p * 0.18) + drift * 0.25 * Math.sign(Math.sin(th) || 1)
    if (fy > floor) continue
    const s = 2.4 + h * 3.6
    const c = Math.cos(h * 6), si = Math.sin(h * 6)
    shockPatch(ctx, [[fx + c * s * 1.8, fy + si * s * 1.8], [fx - si * s * 0.6, fy + c * s * 0.6],
      [fx - c * s * 1.2, fy - si * s * 1.2], [fx + si * s * 0.6, fy - c * s * 0.6]], BRIGHT(), Math.min(1, alpha * 1.2) * (1 - p * 0.5))
  }
}

function drawBrushRing(
  ctx: CanvasRenderingContext2D, x: number, ry: number, alpha: number, p: number, floor: number, seed: number,
): void {
  if (alpha <= 0.01) return
  const rx = ry * 0.5
  const dome: ShockPoint[] = []
  for (let n = 0; n <= 18; n += 1) {
    const th = -Math.PI / 2 + n / 18 * Math.PI
    dome.push([x + rx * Math.cos(th), Math.min(floor, ry * Math.sin(th))])
  }
  dome.push([x - rx * 0.7, Math.min(floor, ry * 0.9)], [x - rx * 0.7, -ry * 0.9])
  refractInside(ctx, dome, 7 + (1 - p) * 9, seed, alpha * 0.9)
  ctx.save()
  ctx.beginPath()
  ctx.rect(x - 4000, -4000, 8000, 4000 + floor)
  ctx.clip()
  // 内側はかすかな紫のにじみだけ (光の塊にしない)。
  shockPatch(ctx, dome, DEEP(), alpha * 0.4)
  const raw = fajinSprites() ? ringSprite(Math.floor(hash01(seed) * 3)) : null
  const img = raw ? etImg(raw) : null
  if (img) {
    // 絵の三日月は膨らみが左 → 左右を返して進む向き (+x) へ膨らませる。弧の弦は x、膨らみの先は x + rx。
    const h = ry * 2.15
    const w = h * (img.width / img.height)
    drawAdditive(ctx, img, x + rx * 0.5, 0, w, h, 0.5, 0.5, alpha * (1.05 - p * 0.2), 0, true)
  } else {
    brushArc(ctx, x, rx, ry, p, alpha, seed, floor)
  }
  ctx.restore()
}

// 通った道の床の土ぼこり (通常合成の灰)。輪が床に届いていなくても、空気の押しで道に沿って立つ。
function drawPathDust(
  ctx: CanvasRenderingContext2D, travelled: number, sinceSeenMs: number, speed: number, floor: number, seed: number,
): void {
  if (floor > 320) return
  const near = 1 - clamp01((floor - 60) / 260)
  const step = 34
  const life = 620
  const first = Math.max(0, Math.floor((travelled - speed * life / 1000) / step))
  ctx.save()
  for (let n = first; n <= Math.floor(travelled / step); n += 1) {
    const back = travelled - n * step
    const age = sinceSeenMs + back / Math.max(1, speed) * 1000
    if (age >= life) continue
    const p = clamp01(age / life)
    const h = hash01(seed + n * 2.7)
    const dx = -back - 10 - p * (20 + h * 30)
    const r = (12 + h * 14) * (0.5 + p * 1.1)
    ctx.globalAlpha = near * clamp01(age / 60) * (1 - p) ** 1.2 * 0.42
    const g = ctx.createRadialGradient(dx, floor - r * 0.4, 0, dx, floor - r * 0.4, r)
    g.addColorStop(0, 'rgba(126,116,136,0.9)')
    g.addColorStop(1, 'rgba(60,54,70,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.ellipse(dx, floor - r * 0.38, r * 1.6, r * 0.55, 0, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

function drawBrushRings(
  ctx: CanvasRenderingContext2D, travelled: number, speed: number, sinceSeenMs: number,
  flightMs: number, floor: number, seed: number,
): void {
  drawPathDust(ctx, travelled, sinceSeenMs, speed, floor, seed)
  // 手本 j4 は輪が大きく (人の背丈を越える)、3〜4枚が同時に見える → v1 の輪より大きく・長く残す。
  const strength = Math.min(1, ringStrength(flightMs) * 1.25)
  const lifeMs = 660 // 描画履歴の 680ms 以内で必ず消える。
  const first = Math.max(0, Math.floor((travelled - speed * lifeMs / 1000) / RINGS.step))
  for (let n = first; n <= Math.floor(travelled / RINGS.step); n += 1) {
    const back = travelled - n * RINGS.step
    const age = sinceSeenMs + back / Math.max(1, speed) * 1000
    if (age >= lifeMs) continue
    const p = clamp01(age / lifeMs)
    const ry = Math.min(RINGS.maxRy * 1.2, (RINGS.baseRy + n * RINGS.step * RINGS.growByPath + age * RINGS.growByAge) * 1.15)
    const a = clamp01(age / 35) * (1 - p) ** 1.1 * strength * (n === 0 ? 0.6 : 1)
    drawBrushRing(ctx, -back - 20, ry, a, p, floor, seed + n * 11.3)
  }
}

// 前端: 先の尖った淡い紫の芯から、後ろへ細い棘が開く (面だけ・白い線にしない)。
function drawPointedFront(ctx: CanvasRenderingContext2D, flightMs: number, seed: number): void {
  const s = ringStrength(flightMs)
  const flare = 1 + 0.5 * (1 - clamp01(flightMs / 90))
  const raw = fajinSprites() ? frontSprite(Math.floor(flightMs / 55 + seed)) : null
  const img = raw ? etImg(raw) : null
  if (img) {
    // 絵の穂先は左向き → 左右を返し、先端 (左端から 4.5%) を前端に合わせる。
    const h = 92 * flare
    drawAdditive(ctx, img, 18 * flare, 0, h * (img.width / img.height), h, FRONT_TIP_X, 0.5, s, 0, true)
    return
  }
  glowMote(ctx, -10, 0, 34 * flare, BRIGHT(), 0.32 * s)
  shockPatch(ctx, [[18 * flare, 0], [-30, -9], [-22, 0], [-30, 9]], PALE(), 0.85 * s)
  for (let k = 0; k < 9; k += 1) {
    const h = hash01(seed + k * 6.1)
    const ang = (k / 8 - 0.5) * 1.5 + (h - 0.5) * 0.15
    const L = (34 + h * 30) * flare
    const ex = -Math.cos(ang) * L, ey = Math.sin(ang) * L
    const w = 2.2 + h * 2.4
    const nx = -Math.sin(ang) * w, ny = -Math.cos(ang) * w
    shockPatch(ctx, [[4, 0], [ex * 0.5 + nx, ey * 0.5 + ny], [ex, ey], [ex * 0.5 - nx, ey * 0.5 - ny]],
      Math.abs(ang) < 0.3 ? PALE() : BRIGHT(), (0.5 + h * 0.4) * s)
  }
}

type WindFrame ={ time: number; look: FajinLook; traces: Map<number, WindTrace> }
const windFrames = new WeakMap<CanvasRenderingContext2D, WindFrame>()

// renderから毎フレーム1回。hitが削除されても描画だけ最大680ms保持する。
// owner=この描画履歴。phase離脱・ボス死亡/変更・時刻巻戻し・候補切替では即破棄する。
export function drawFajinWindFrame(
  ctx: CanvasRenderingContext2D,
  state: Pick<GameState, 'hits' | 'time' | 'phase' | 'boss'>,
  look: FajinLook,
): void {
  if (state.phase !== 'playing' || state.boss.id !== 'the-executioner' || state.boss.hp <= 0
    || (look !== 'e' && look !== 'f' && look !== 'g' && look !== 'h' && look !== 'i' && look !== 'j')) {
    windFrames.delete(ctx)
    return
  }
  let frame = windFrames.get(ctx)
  if (!frame || state.time < frame.time || frame.look !== look) {
    frame = { time: state.time, look, traces: new Map() }
    windFrames.set(ctx, frame)
  }
  frame.time = state.time
  const live = new Set<number>()
  for (const hit of state.hits) {
    if (!isExecutionerFajin(hit.attackId) || hit.follow !== 'travel' || hit.consumed
      || hit.x === undefined || hit.y === undefined || hit.vx === undefined || hit.widthPx === undefined) continue
    live.add(hit.id)
    const previous = frame.traces.get(hit.id)
    const flight = Math.max(0, hit.elapsedMs - (hit.launchDelayMs ?? 0))
    const reset = !previous || hit.elapsedMs < previous.hit.elapsedMs
      || hit.reflected !== previous.hit.reflected || Math.sign(hit.vx) !== Math.sign(previous.hit.vx ?? 0)
    // 通常は保持時の位置を固定保存。途中から描く時だけ現在位置から推定する。
    // 反射は現在位置を新しい起点にして、旧方向の道を逆算して飛び移らせない。
    const originX = previous && !reset
      ? previous.originX
      : hit.reflected ? hit.x : hit.x - hit.vx * flight / 1000
    frame.traces.set(hit.id, { hit: { ...hit }, originX, seenAt: state.time })
    drawFajinWave(ctx, hit, state.time, look, originX, state.boss.x)
  }
  for (const [id, trace] of frame.traces) {
    if (live.has(id)) continue
    const age = (state.time - trace.seenAt) * 1000
    const h = trace.hit
    if (age >= WIND.residueMs || h.elapsedMs < (h.launchDelayMs ?? 0)) {
      frame.traces.delete(id)
      continue
    }
    const dir = h.vx! >= 0 ? 1 : -1
    ctx.save()
    ctx.translate(h.x!, h.y!)
    ctx.scale(dir, 1)
    if (look === 'j') {
      ;(execFxV1() ? drawStackedRings : drawBrushRings)(ctx, Math.max(0, (h.x! - trace.originX) * dir), Math.abs(h.vx!), age,
        Math.max(0, h.elapsedMs - (h.launchDelayMs ?? 0)) + age, ARENA.floorY - h.y!, h.id * 7.91)
    } else if (look === 'i') {
      drawCompressionWake(ctx, Math.max(0, (h.x! - trace.originX) * dir), Math.abs(h.vx!), age)
    } else if (look === 'h') {
      drawUnfoldingPath(ctx, Math.max(0, (h.x! - trace.originX) * dir), Math.abs(h.vx!), age)
    } else if (look === 'g') {
      drawPressureWake(ctx, Math.max(0, (h.x! - trace.originX) * dir), Math.abs(h.vx!), age, h.id * 7.91)
    } else {
      drawWindWake(ctx, look, ARENA.floorY - h.y!, Math.max(0, (h.x! - trace.originX) * dir),
        Math.abs(h.vx!), age, h.id * 7.91, (state.boss.x - h.x!) * dir)
    }
    ctx.restore()
  }
}

// ───────────────────────── 溜め (elapsedMs < launchDelayMs・刃先に留まる) ─────────────────────────

function drawFajinCharge(
  ctx: CanvasRenderingContext2D, look: Exclude<FajinLook, 'old' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j'>,
  cx: number, cy: number, widthPx: number, p: number, elapsedMs: number, launchDelayMs: number, seed: number,
  dirX: 1 | -1,
): void {
  if (look === 'a') {
    // 収縮: 大きく薄い白紫の靄が p に従って縮んで濃くなる。粒なし。
    // 🔵 2026-09-09 便2差し替え: 実機で旧・紫玉の5分の1しか見えなかったため寸法とαを底上げ。
    const r = lerp(widthPx * 2.4, widthPx * 0.7, p)
    const a = lerp(0.22, 1.0, p)
    glowMote(ctx, cx, cy, r * 1.5, BRIGHT(), a * 0.4)
    glowMote(ctx, cx, cy, r, PALE(), a)
  } else if (look === 'b') {
    // 収束: 円周 (半径 widthPx×3.5) からランダムな10粒が刃先へ吸い込まれ、到着で消える。
    //   芯は p² で明るく育つ (半径0→widthPx×0.9・α0→0.95)。
    const ringR = widthPx * 3.5
    for (let i = 0; i < 10; i += 1) {
      const arrivalP = 0.4 + hash01(seed + i + 50) * 0.6
      if (p >= arrivalP) continue
      const ang = hash01(seed + i) * Math.PI * 2
      const startX = cx + Math.cos(ang) * ringR
      const startY = cy + Math.sin(ang) * ringR
      const t = p / arrivalP
      const x = lerp(startX, cx, t)
      const y = lerp(startY, cy, t)
      const r = 3.5 + hash01(seed + i + 3) * 2.5
      glowMote(ctx, x, y, r, BRIGHT(), (1 - t) * 0.7 + 0.3)
    }
    const coreR = widthPx * 0.9 * (p * p)
    const coreA = 0.95 * (p * p)
    if (coreR > 0.3) {
      glowMote(ctx, cx, cy, coreR * 1.4, BRIGHT(), coreA * 0.5)
      glowMote(ctx, cx, cy, coreR, whiteA(1), coreA)
    }
  } else if (look === 'c') {
    // 脈動: 芯 (半径 widthPx×0.9) が2回ゆっくり膨らんで縮み (sin・周期380ms・振幅±25%)、
    //   最後の120msで一気に半径×0.25へ凝縮しα1.0。
    const baseR = widthPx * 0.9
    const finalStart = Math.max(0, launchDelayMs - 120)
    const fp = clamp01((elapsedMs - finalStart) / 120)
    const pulseR = baseR * (1 + 0.25 * Math.sin((2 * Math.PI * elapsedMs) / 380))
    const r = lerp(pulseR, baseR * 0.25, fp)
    const a = lerp(0.5, 1.0, fp)
    glowMote(ctx, cx, cy, r * 1.8, PALE(), a * 0.45)
    glowMote(ctx, cx, cy, r, DEEP(), a)
  } else {
    // d (2026-09-10・参考画像の写し): p>0.7 で前方へ圧が漏れる細い光 +
    //   p>0.85 で刃先の輪が150ms周期で内側へ縮む。
    // 🔵 2026-09-10 ユーザー裁定: 溜め中に弾が集まる演出は不採用（玉に見えるため）。
    if (p > 0.7) {
      const leakLen = widthPx * 3 * (p - 0.7) / 0.3
      const leakA = (p - 0.7) / 0.3
      blobEllipse(ctx, cx + dirX * leakLen / 2, cy, leakLen / 2, widthPx * 0.08, whiteA(1), leakA)
    }
    if (p > 0.85) {
      const cycleT = ((elapsedMs % 150) + 150) % 150 / 150
      const ringRadius = lerp(widthPx * 1.4, widthPx * 0.6, cycleT)
      const ringA = lerp(0.6, 0, cycleT) * Math.min(1, (p - 0.85) / 0.05)
      ctx.save()
      ctx.translate(cx, cy)
      ctx.scale(0.38, 1)
      ringGlow(ctx, 0, 0, ringRadius, widthPx * 0.18, whiteA(1), ringA)
      ctx.restore()
    }
  }
}

// ───────────────────────── 飛行 (elapsedMs >= launchDelayMs・vx で水平に飛ぶ) ─────────────────────────

// 発射の瞬間 (flight<140ms) に発射点で焚かれる閃光 (面)。4案共通・主色だけ差し替える。
//   🔵 2026-09-09 便2差し替え: 実機で旧・紫玉に比べ見えなかったための新規追加。
function drawLaunchFlash(
  ctx: CanvasRenderingContext2D, originX: number, originY: number, widthPx: number, flight: number, mainColor: string,
): void {
  if (flight >= 140) return
  const fp = flight / 140
  const r1 = widthPx * 1.6 * (1 + fp * 0.6)
  glowMote(ctx, originX, originY, r1, whiteA(1), (1 - fp) * 0.9)
  glowMote(ctx, originX, originY, r1 * 2.2, mainColor, (1 - fp) * 0.5)
}

function drawFajinFlight(
  ctx: CanvasRenderingContext2D, look: Exclude<FajinLook, 'old' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j'>, hit: ActiveHit,
  widthPx: number, dirX: 1 | -1, flight: number, seed: number, reflected: boolean,
): void {
  const cx = hit.x as number
  const cy = hit.y as number
  const vx = hit.vx as number
  // flight 0ms で α1.0、600ms で 0.55 まで線形に落ちる (4案共通)。
  const flightAlpha = lerp(1, 0.55, flight / 600)
  // 発射点 (発射時刻=launchDelayMs 基準・vx で逆算。垂直方向はこの技は水平飛行のみ)。
  const originX = cx - vx * (flight / 1000)
  const originY = cy
  // 芯: 進行方向に細長い楕円。全長 widthPx×3.2・全幅 widthPx×0.9 (半径はその半分)。a/b/c だけが使う
  //   (d は自前の芯線寸法を持つ)。
  //   🔵 2026-09-09 便2差し替え: 実機で旧・紫玉の5分の1しか見えなかったため寸法を底上げ (widthPx比は不変)。
  const rx = widthPx * 1.6
  const ry = widthPx * 0.45
  const flashColor = look === 'a' ? PALE() : look === 'b' ? BRIGHT() : look === 'c' ? DEEP() : brightD()
  drawLaunchFlash(ctx, originX, originY, widthPx, flight, flashColor)

  if (look === 'a') {
    // リング: 前へ凸の半円が3枚、発射点から順に生まれて膨らみ薄れる (寿命220ms・半径0.9→3.6×widthPx)。
    //   🔵 2026-09-09 便2差し替え: 半径・厚み・αピークを底上げ (寿命・生成間隔は不変)。
    for (let i = 0; i < 3; i += 1) {
      const age = flight - i * 90
      if (age < 0 || age >= 220) continue
      const lifeP = age / 220
      const ringR = lerp(widthPx * 0.9, widthPx * 3.6, lifeP)
      const ringA = (1 - lifeP) * 0.9 * flightAlpha
      ringGlow(ctx, originX, originY, ringR, widthPx * 0.55, i % 2 === 0 ? PALE() : BRIGHT(), ringA, dirX)
    }
    // 芯: 白紫主・縁だけ明るい紫。
    blobEllipse(ctx, cx, cy, rx * 1.25, ry * 1.35, BRIGHT(), 0.5 * flightAlpha)
    blobEllipse(ctx, cx, cy, rx, ry, PALE(), 0.85 * flightAlpha)
    glowMote(ctx, cx, cy, ry * 1.6, whiteA(0.6), Math.min(1, 0.65 * flightAlpha))
    // 飛行中の粒: 芯の後ろへ4粒がゆっくり離れる。
    for (let i = 0; i < 4; i += 1) {
      const speed = 0.5 + hash01(seed + i) * 0.4
      const life = (flight / 1000 * speed + hash01(seed + i + 9)) % 1
      const back = rx + life * 40
      const side = (hash01(seed + i + 3) - 0.5) * ry * 2.4
      glowMote(ctx, cx - dirX * back, cy + side, 3 + hash01(seed + i + 5) * 3, PALE(), (1 - life) * 0.7 * flightAlpha)
    }
    // 発射の瞬間: 上下 (法線方向) へ14粒を横に弾く。
    emberBurst(ctx, originX, originY, 14, PALE(), flight / 1000, 0.28, seed, 70, 260)
  } else if (look === 'b') {
    // リング: 正円の同心リング3枚。発射点を中心に生まれてその場に残って膨らみ薄れる
    //   (寿命260ms・半径0.8→4.5×widthPx)。芯だけが前へ抜けていく。
    //   🔵 2026-09-09 便2差し替え: 半径・厚み・αピークを底上げ (寿命・生成間隔は不変)。
    for (let i = 0; i < 3; i += 1) {
      const age = flight - i * 100
      if (age < 0 || age >= 260) continue
      const lifeP = age / 260
      const ringR = lerp(widthPx * 0.8, widthPx * 4.5, lifeP)
      const ringA = (1 - lifeP) * 0.85 * flightAlpha
      ringGlow(ctx, originX, originY, ringR, widthPx * 0.5, BRIGHT(), ringA)
    }
    // 芯: 明るい紫主・中心だけ白。
    blobEllipse(ctx, cx, cy, rx * 1.1, ry * 1.1, BRIGHT(), 0.85 * flightAlpha)
    glowMote(ctx, cx, cy, ry * 1.6, whiteA(0.8), Math.min(1, 0.91 * flightAlpha))
    // 発射の瞬間: 進行方向へ扇状に18粒。飛行中の粒はなし。
    emberBurst(ctx, originX, originY, 18, BRIGHT(), flight / 1000, 0.3, seed + 11, 90, 380)
  } else if (look === 'c') {
    // 縦長の楕円の壁 (幅 widthPx×0.8) が1枚だけ、芯と一緒に前へ進みながら300msで高さ×4.0まで伸びて薄れる。
    //   🔵 2026-09-09 便2差し替え: 幅とαピークを底上げ (高さ倍率・寿命は不変)。
    if (flight <= 300) {
      const wp = flight / 300
      const wallH = lerp(widthPx * 2.6, widthPx * 2.6 * 4.0, wp)
      const wallA = lerp(0.85, 0, wp) * flightAlpha
      blobEllipse(ctx, cx, cy, widthPx * 0.4, wallH / 2, PALE(), wallA)
    }
    // 芯: 深紫の芯を白紫の縁が包む (芯が暗い)。
    blobEllipse(ctx, cx, cy, rx * 1.2, ry * 1.3, PALE(), 0.55 * flightAlpha)
    blobEllipse(ctx, cx, cy, rx * 0.75, ry * 0.75, DEEP(), 0.9 * flightAlpha)
    // 発射の瞬間: 進行方向やや後ろへ8粒だけ (少なく重い)。飛行中は無し。
    emberBurst(ctx, originX, originY, 8, DEEP(), flight / 1000, 0.3, seed + 21, 20, 120)
  } else {
    // d (2026-09-10・参考画像の写し): O(発射点)〜T(先端=現在位置) の全域を貫く芯線 + 40msごとに
    //   その場に残る「角笛の輪」(横に潰した円) + 先端で30msごとに入れ替わる扇状の火花 + O〜T を
    //   先端へ流れる粒。tint (violet/gold) で PALE()/BRIGHT() を差し替える (d だけ)。
    // 🔵 数値は参考画像から寸法だけ読み取れず票にも無いため、この場で決めた (粒の大きさ・輪の枚数上限等)。
    const tipExtendX = cx + dirX * widthPx * 1.5
    let backX = originX
    if (flight > 500) {
      const cappedLen = Math.min(Math.abs(tipExtendX - backX), widthPx * 12)
      backX = tipExtendX - dirX * cappedLen
    }
    const coreLen = Math.abs(tipExtendX - backX)
    const coreCx = (backX + tipExtendX) / 2

    // 1. 芯線: 白の極細 → 淡い層 → 靄の3層。
    blobEllipse(ctx, coreCx, cy, coreLen / 2, widthPx * 0.10, whiteA(1), 1.0 * flightAlpha)
    blobEllipse(ctx, coreCx, cy, coreLen / 2, widthPx * 0.45, paleD(), 0.55 * flightAlpha)
    blobEllipse(ctx, coreCx, cy, coreLen / 2, widthPx * 1.1, brightD(), 0.25 * flightAlpha)

    // 2. 角笛の輪: 40msごとに1枚、その時刻の先端位置にその場で生まれ、寿命360msで
    //   膨らんで消える (360/40=9枚が自然に上限)。横潰し0.38・周上に6粒の火花つき。
    const speed = Math.abs(vx) / 1000 // px/ms
    const kMax = Math.floor(flight / 40)
    for (let k = Math.max(0, kMax - 8); k <= kMax; k += 1) {
      const tSpawn = k * 40
      const age = flight - tSpawn
      if (age < 0 || age >= 360) continue
      const lifeP = age / 360
      const ringX = originX + dirX * speed * tSpawn
      const ringRadius = lerp(widthPx * 0.5, widthPx * 3.2, lifeP)
      const ringA = lerp(0.9, 0, lifeP) * flightAlpha
      const ringColor = lifeP < 0.5
        ? mixColor(WHITE_HEX(), paleD(), lifeP * 2, ringA)
        : mixColor(paleD(), brightD(), (lifeP - 0.5) * 2, ringA)
      ctx.save()
      ctx.translate(ringX, cy)
      ctx.scale(0.38, 1)
      ringGlow(ctx, 0, 0, ringRadius, widthPx * 0.45, ringColor, 1)
      ctx.restore()
      for (let j = 0; j < 6; j += 1) {
        const ang = hash01(seed + k * 13 + j) * Math.PI * 2
        const px = ringX + Math.cos(ang) * ringRadius * 0.38
        const py = cy + Math.sin(ang) * ringRadius
        const gr = 2.5 + hash01(seed + k * 13 + j + 5) * 1.5
        glowMote(ctx, px, py, gr, whiteA(1), 0.8 * (1 - lifeP) * flightAlpha)
      }
    }

    // 3. 先端の火花: T (現在位置) + 30msごとに入れ替わる扇状の火花22個 (進行方向 ±65°・チラつく)。
    // 先端＝衝撃波の前面。玉ではなく、進行方向へ潰した縦長の輪1枚が波と一緒に進む。
    // 🔵 2026-09-10 ユーザー裁定「弾じゃなくて衝撃波」。
    ctx.save()
    ctx.translate(cx, cy)
    ctx.scale(0.30, 1)
    ringGlow(ctx, 0, 0, widthPx * 1.7, widthPx * 0.5, whiteA(1), 0.95 * flightAlpha)
    ringGlow(ctx, 0, 0, widthPx * 2.4, widthPx * 0.7, brightD(), 0.45 * flightAlpha)
    ctx.restore()
    const forwardAngle = dirX > 0 ? 0 : Math.PI
    const flickerBucket = Math.floor(flight / 30)
    for (let i = 0; i < 22; i += 1) {
      const angJitter = (hash01(seed + i + flickerBucket) - 0.5) * (130 * Math.PI / 180)
      const ang = forwardAngle + angJitter
      const dist = widthPx * (0.4 + hash01(seed + i + flickerBucket + 50) * 2.2)
      const px = cx + Math.cos(ang) * dist
      const py = cy + Math.sin(ang) * dist
      const prx = 5 + hash01(seed + i + 3) * 4
      const pry = 1.5 + hash01(seed + i + 4) * 1.0
      const sparkColor = hash01(seed + i + 9) < 0.5 ? whiteA(0.95) : paleD()
      blobEllipse(ctx, px, py, prx, pry, sparkColor, 0.9 * flightAlpha)
    }

    // 4. 流れる粒: O〜T の間を18個の粒が先端へ流れる (y は粒ごとに固定・x だけ周期移動)。
    const flowLen = Math.max(widthPx, Math.abs(cx - originX))
    for (let i = 0; i < 18; i += 1) {
      const ySign = hash01(seed + i + 200) < 0.5 ? -1 : 1
      const yMag = widthPx * (0.3 + hash01(seed + i + 210) * 2.5)
      const py = cy + ySign * yMag
      const speedMul = 0.6 + hash01(seed + i + 220) * 0.5
      const phase = hash01(seed + i + 230) * 400
      const xLocal = (flight * speedMul + phase) % flowLen
      const px = originX + dirX * xLocal
      const prx = 8 + hash01(seed + i + 240) * 6
      blobEllipse(ctx, px, py, prx, 1.5, paleD(), 0.5 * flightAlpha)
    }
  }

  // 反射後: 芯を白紫寄りに (向きは vx の符号=dirX に自然に従う。4案共通の追い掛け層)。
  if (reflected) {
    blobEllipse(ctx, cx, cy, rx * 0.6, ry * 0.65, whiteA(0.8), 0.6 * flightAlpha)
  }
}

// hit の状態 (elapsedMs / launchDelayMs) から溜め/飛行を自分で判別して描く。
export function drawFajinWave(
  ctx: CanvasRenderingContext2D, hit: ActiveHit, timeSec: number, look: Exclude<FajinLook, 'old'>,
  windOriginX?: number, windBossX?: number,
): void {
  if (hit.x === undefined || hit.y === undefined || hit.vx === undefined || hit.widthPx === undefined) {
    throw new Error(`[fajin] wave hit missing x/y/vx/widthPx: ${hit.attackId}`)
  }
  void timeSec // 全案 hit.elapsedMs だけで決定的に描く。停止中に壁時計で動かさない。
  const widthPx = hit.widthPx
  const dirX: 1 | -1 = hit.vx >= 0 ? 1 : -1
  const launchDelayMs = hit.launchDelayMs ?? 0
  // The current blade/cannon look stays invisible until launch. The old
  // compression mark read as a stray residue after the swing (09-16 feedback).
  if (look === 'i' && hit.elapsedMs < launchDelayMs) return
  const reflected = hit.reflected === true
  const seed = hit.id * 7.91
  ctx.save()
  if (look === 'j') {
    const flight = Math.max(0, hit.elapsedMs - launchDelayMs)
    const originX = windOriginX ?? (reflected ? hit.x : hit.x - hit.vx * flight / 1000)
    ctx.translate(hit.x, hit.y)
    ctx.scale(dirX, 1)
    if (hit.elapsedMs < launchDelayMs) {
      drawRingHold(ctx, hit.elapsedMs, launchDelayMs)
    } else {
      if (execFxV1()) {
        drawStackedRings(ctx, Math.max(0, (hit.x - originX) * dirX), Math.abs(hit.vx), 0, flight,
          ARENA.floorY - hit.y, seed)
        drawRingFront(ctx, flight)
      } else {
        drawBrushRings(ctx, Math.max(0, (hit.x - originX) * dirX), Math.abs(hit.vx), 0, flight,
          ARENA.floorY - hit.y, seed)
        drawPointedFront(ctx, flight, seed)
      }
    }
    ctx.restore()
    return
  }
  if (look === 'i') {
    const flight = Math.max(0, hit.elapsedMs - launchDelayMs)
    const originX = windOriginX ?? (reflected ? hit.x : hit.x - hit.vx * flight / 1000)
    ctx.translate(hit.x, hit.y)
    ctx.scale(dirX, 1)
    if (hit.elapsedMs < launchDelayMs) {
      drawCompressionHold(ctx, hit.elapsedMs)
    } else {
      drawCompressionWake(ctx, Math.max(0, (hit.x - originX) * dirX), Math.abs(hit.vx), 0)
      drawCompressionFront(ctx, flight)
    }
    ctx.restore()
    return
  }
  if (look === 'h') {
    const flight = Math.max(0, hit.elapsedMs - launchDelayMs)
    const originX = windOriginX ?? (reflected ? hit.x : hit.x - hit.vx * flight / 1000)
    ctx.translate(hit.x, hit.y)
    ctx.scale(dirX, 1)
    if (hit.elapsedMs < launchDelayMs) {
      drawUnfoldingHold(ctx, hit.elapsedMs)
    } else {
      drawUnfoldingPath(ctx, Math.max(0, (hit.x - originX) * dirX), Math.abs(hit.vx), 0)
      // 判定が今ある場所の薄い前縁。後方の展開した面より明瞭にする。
      unfoldingMembrane(ctx, -43, 0, 48, 76, -0.05, -1.48, 2.96, 0.82, 0.7)
    }
    ctx.restore()
    return
  }
  if (look === 'g') {
    const flight = Math.max(0, hit.elapsedMs - launchDelayMs)
    const originX = windOriginX ?? (reflected ? hit.x : hit.x - hit.vx * flight / 1000)
    ctx.translate(hit.x, hit.y)
    ctx.scale(dirX, 1)
    if (hit.elapsedMs < launchDelayMs) {
      drawPressureHold(ctx, hit.elapsedMs, launchDelayMs)
    } else {
      drawPressureWake(ctx, Math.max(0, (hit.x - originX) * dirX), Math.abs(hit.vx), 0, seed)
      drawPressureFront(ctx, flight, seed)
    }
    ctx.restore()
    return
  }
  if (look === 'e' || look === 'f') {
    const flight = Math.max(0, hit.elapsedMs - launchDelayMs)
    const originX = windOriginX ?? (reflected ? hit.x : hit.x - hit.vx * flight / 1000)
    const travelled = Math.max(0, (hit.x - originX) * dirX)
    ctx.translate(hit.x, hit.y)
    ctx.scale(dirX, 1)
    const floor = ARENA.floorY - hit.y
    const bossX = ((windBossX ?? originX) - hit.x) * dirX
    if (hit.elapsedMs < launchDelayMs) {
      drawWindHold(ctx, look, floor, clamp01(hit.elapsedMs / Math.max(1, launchDelayMs)), seed, bossX)
    } else {
      drawWindWake(ctx, look, floor, travelled, Math.abs(hit.vx), 0, seed, bossX)
      drawWindFlight(ctx, look, floor, flight, seed, bossX)
    }
    ctx.restore()
    return
  }
  if (hit.elapsedMs < launchDelayMs) {
    const p = clamp01(hit.elapsedMs / Math.max(1, launchDelayMs))
    drawFajinCharge(ctx, look, hit.x, hit.y, widthPx, p, hit.elapsedMs, launchDelayMs, seed, dirX)
  } else {
    const flight = hit.elapsedMs - launchDelayMs
    drawFajinFlight(ctx, look, hit, widthPx, dirX, flight, seed, reflected)
  }
  ctx.restore()
}
